import React, { useState, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

export default function Campaigns() {
  const [form, setForm] = useState({ subject: "", content: "", schedule_time: "" });
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [templates, setTemplates] = useState([]);
  const [campaignList, setCampaignList] = useState([]);
  const [search, setSearch] = useState("");

  // ✅ Load templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const res = await fetch("https://app.saravanabhava.in/api/templates.php");
        const data = await res.json();

        if (data.status === "ok" && Array.isArray(data.data)) {
          setTemplates(data.data); // only the array
        } else {
          setTemplates([]);
          console.error("Error loading templates:", data.message);
        }
      } catch (err) {
        console.error("Error loading templates", err);
      }
    };
    loadTemplates();
  }, []);

  // ✅ TipTap
  const editor = useEditor({
    extensions: [StarterKit],
    content: form.content || "<p>Start writing...</p>",
    onUpdate: ({ editor }) => {
      setForm({ ...form, content: editor.getHTML() });
    },
  });

  // ✅ Load campaigns
  const loadCampaigns = async () => {
    try {
      const res = await fetch("https://app.saravanabhava.in/api/campaigns.php");
      const data = await res.json();

      if (Array.isArray(data)) {
        setCampaignList(data);
      } else if (data.status === "ok" && Array.isArray(data.data)) {
        setCampaignList(data.data);
      } else {
        setCampaignList([]);
      }
    } catch (err) {
      console.error("Error loading campaigns", err);
    }
  };

  useEffect(() => {
    loadCampaigns();
  }, []);

  // ✅ Template select
  const handleTemplateChange = (e) => {
    const templateName = e.target.value;
    setSelectedTemplate(templateName);

    const template = templates.find((t) => t.name === templateName);
    if (template) {
      setForm({ ...form, content: template.content });
      editor?.commands.setContent(template.content);
    }
  };

  // ✅ Create campaign
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await fetch("https://app.saravanabhava.in/api/campaigns.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      setForm({ subject: "", content: "", schedule_time: "" });
      setSelectedTemplate("");
      editor?.commands.setContent("<p></p>");
      loadCampaigns();
      alert("Campaign created successfully!");
    } catch (err) {
      console.error(err);
      alert("Error creating campaign.");
    }
  };

  // ✅ Start sending
  const startSending = async (id) => {
    try {
      await fetch(`https://app.saravanabhava.in/backend/campaign_build_queue.php?campaign_id=${id}`);
      alert("Campaign sending started (queue built).");
      loadCampaigns();
    } catch (err) {
      console.error(err);
      alert("Error starting campaign.");
    }
  };

  // ✅ Delete campaign
  const deleteCampaign = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    try {
      await fetch(`https://app.saravanabhava.in/api/campaigns.php?id=${id}`, {
        method: "DELETE",
      });
      loadCampaigns();
    } catch (err) {
      console.error(err);
      alert("Error deleting campaign.");
    }
  };

  // ✅ Filter campaigns
  const filteredCampaigns = campaignList.filter(
    (c) =>
      c.subject?.toLowerCase().includes(search.toLowerCase()) ||
      c.content?.toLowerCase().includes(search.toLowerCase())
  );

  const statusBadge = (status) => {
    const colors = {
      draft: "bg-gray-200 text-gray-800",
      sending: "bg-yellow-200 text-yellow-800",
      completed: "bg-green-200 text-green-800",
    };
    return (
      <span className={`px-2 py-1 rounded text-sm ${colors[status] || "bg-gray-100"}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="space-y-6 p-6 bg-white rounded-xl shadow">
      <h1 className="text-2xl font-bold">Campaigns</h1>

      {/* ✅ Create Form */}
      <form onSubmit={handleSubmit} className="space-y-4 border-b pb-6">
        <h2 className="text-xl font-semibold">Create New Campaign</h2>

        <input
          type="text"
          className="border p-2 rounded w-full"
          placeholder="Subject"
          value={form.subject}
          onChange={(e) => setForm({ ...form, subject: e.target.value })}
          required
        />

        <select
          className="border p-2 rounded w-full"
          value={selectedTemplate}
          onChange={handleTemplateChange}
        >
          <option value="">-- Select a Template --</option>
          {templates.map((t) => (
            <option key={t.name} value={t.name}>
              {t.name}
            </option>
          ))}
        </select>

        <div className="border rounded p-2 bg-white min-h-[200px]">
          <EditorContent editor={editor} />
        </div>

        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600">Schedule (optional)</label>
          <input
            type="datetime-local"
            className="border p-2 rounded"
            value={form.schedule_time}
            onChange={(e) => setForm({ ...form, schedule_time: e.target.value })}
          />
        </div>

        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Create Campaign
        </button>
      </form>

      {/* ✅ Search */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">All Campaigns</h2>
        <input
          type="text"
          placeholder="Search campaigns..."
          className="border p-2 rounded"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* ✅ Campaigns List */}
      <div className="overflow-x-auto">
        <table className="w-full border">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 border">ID</th>
              <th className="p-2 border">Subject</th>
              <th className="p-2 border">Status</th>
              <th className="p-2 border">Schedule</th>
              <th className="p-2 border">Content Preview</th>
              <th className="p-2 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCampaigns.map((c) => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="p-2 border">{c.id}</td>
                <td className="p-2 border">{c.subject}</td>
                <td className="p-2 border">{statusBadge(c.status)}</td>
                <td className="p-2 border">{c.schedule_time || "-"}</td>
                <td className="p-2 border max-w-xs overflow-hidden">
                  <div
                    className="line-clamp-2"
                    dangerouslySetInnerHTML={{ __html: c.content }}
                  />
                </td>
                <td className="p-2 border space-x-2">
                  {c.status === "draft" && (
                    <button
                      onClick={() => startSending(c.id)}
                      className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                    >
                      Start
                    </button>
                  )}
                  <button
                    onClick={() => deleteCampaign(c.id)}
                    className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {filteredCampaigns.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center p-4 text-gray-500">
                  No campaigns found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
